import {
  cloneElement,
  HTMLProps,
  isValidElement,
  ReactElement,
  ReactNode,
  useContext,
} from 'react';
import { FieldSubscription } from 'final-form';
import { FormFieldError } from 'app/shared/models/form';
import { U21HelpTooltipProps } from 'app/shared/u21-ui/components/display/U21HelpTooltip';

import { getDOMProps } from 'app/shared/utils/react';
import { isEqual, isUndefined } from 'lodash';
import styled from 'styled-components';
import useId from '@mui/utils/useId';

import {
  Field as ReactFormField,
  FieldInputProps,
  FieldRenderProps,
} from 'react-final-form';
import { FormControl } from '@mui/material';
import { U21FormContext } from 'app/shared/u21-ui/components/form/U21FormContext';
import { U21FormError } from 'app/shared/u21-ui/components/form/U21FormError';
import { U21FormLabel } from 'app/shared/u21-ui/components/form/U21FormLabel';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';

export interface U21FormFieldCustomInputProps<FieldValue> extends Omit<
  FieldInputProps<FieldValue>,
  'onChange' | 'value'
> {
  onChange: (value: FieldValue) => void;
  value?: FieldValue;
}

/** @deprecated - use function children instead */
type DeprecatedReactElement = ReactElement;
export interface U21FormFieldCustomProps<FieldValue, FormValues> extends Omit<
  HTMLProps<HTMLDivElement>,
  'children' | 'label' | 'type'
> {
  children:
    | DeprecatedReactElement
    | ((props: U21FormFieldCustomInputProps<FieldValue>) => ReactNode);
  description?: string;
  disabled?: boolean;
  fieldProps?: Record<string, any>;
  help?: U21HelpTooltipProps['help'];
  hidden?: boolean;
  label?: ReactNode;
  name: string;
  required?: boolean;
  subscription?: FieldSubscription;
  suppressError?: boolean;
  allowFalsyValue?: boolean;
  validate?: (value: FieldValue, allValues: FormValues) => FormFieldError;
}

export const U21FormFieldCustom = <
  FieldValue,
  FormValues extends object = Record<string, any>,
>({
  children,
  description,
  disabled: disabledProp,
  fieldProps,
  help,
  hidden,
  label,
  name,
  required,
  subscription,
  suppressError,
  validate,
  allowFalsyValue = false,
  ...rest
}: U21FormFieldCustomProps<FieldValue, FormValues>) => {
  const { disabled: disabledContext, loading } = useContext(U21FormContext);
  const id = useId(fieldProps?.id);

  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;
  const allowFalsyValueInternally =
    allowFalsyValue ||
    (!isUndefined(fieldProps?.defaultClearableValue) &&
      !fieldProps?.defaultClearableValue); // Want to allow falsy value if flag is true or if defaultClearableValue is set but falsy

  return (
    <ReactFormField
      isEqual={isEqual}
      name={name}
      subscription={{
        error: true,
        invalid: true,
        touched: true,
        value: true,
        ...subscription,
      }}
      validate={validate}
      parse={allowFalsyValueInternally ? (value) => value : undefined} // This is to prevent "" being overriden with undefined by React Final Form
    >
      {({ input, meta }: FieldRenderProps<FieldValue>) => {
        if (hidden) {
          return null;
        }
        const error = meta.touched && meta.invalid;
        const disabled = disabledContext || disabledProp || loading;
        const inputProps = {
          'aria-describedby': `${errorId} ${descriptionId}`,
          disabled,
          error,
          id,
          required,
          ...fieldProps,
          ...input,
          onChange: (value, ...args) => {
            input?.onChange(value);
            fieldProps?.onChange?.(value, ...args);
          },
          value:
            !allowFalsyValueInternally && input.value === ''
              ? undefined
              : input.value,
        };
        return (
          <div {...getDOMProps(rest)}>
            <FormControl error={error} fullWidth>
              {Boolean(label) && (
                <StyledU21FormLabel
                  $hasDescription={Boolean(description)}
                  disabled={disabled}
                  id={`${id}-label`}
                  error={error}
                  help={help}
                  htmlFor={id}
                  required={required}
                >
                  {label}
                </StyledU21FormLabel>
              )}
              {description && (
                <StyledU21Typography
                  aria-hidden
                  id={descriptionId}
                  color="text.secondary"
                  variant="body2"
                >
                  {description}
                </StyledU21Typography>
              )}
              {isValidElement<any>(children)
                ? cloneElement(children, inputProps)
                : children(inputProps)}
              {!suppressError && (
                <U21FormError id={errorId} error={error && meta.error} />
              )}
            </FormControl>
          </div>
        );
      }}
    </ReactFormField>
  );
};

const StyledU21FormLabel = styled(U21FormLabel)<{ $hasDescription?: boolean }>`
  margin-bottom: ${(props) => (props.$hasDescription ? 0 : 8)}px;
`;

const StyledU21Typography = styled(U21Typography)`
  margin-bottom: 8px;
`;
